import { useMutation, useQuery } from "@tanstack/react-query";
import { FormikProvider, useFormik } from "formik";
import { apiV2, queryClient } from "libs/api/src/lib/apiV2";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../hooks";
import { ModalContext } from "../../../providers";
import { ToastContext } from "../../../providers/ToastProvider/context";
import { RouteFeature, RouteLayout } from "../../../routes";
import { Button, FormInput } from "../../atoms";
import { FormPasswordInput } from "../../atoms/form/text/FormPasswordInput";
import { DeleteMemberModal } from "../../templates/DeleteMemberModal";
import { MemberEmailSchema } from "./MemberEmailSchema";

export const MemberEmailForm = ({ memberId }: { memberId: string }) => {
  const history = useHistory();
  const { createClubLink } = usePortalRoutes();
  const { data: member } = useQuery(apiV2.staff.member.queries.id(memberId));
  const { t } = useTranslation();
  const updateEmailAndPasswordMutation = useMutation(
    apiV2.staff.member.mutations.updateEmailAndPassword(),
  );

  const schema = useMemo(() => {
    return MemberEmailSchema(
      member?.email ?? "",
      async (email) =>
        await queryClient.fetchQuery(
          apiV2.hosted.member.queries.emailExists(email),
        ),
    );
  }, [member?.email]);
  const formik = useFormik({
    initialValues: {
      email: member?.email ?? "",
      password: "",
      passwordConfirm: "",
    },
    onSubmit: async (values) => {
      await updateEmailAndPasswordMutation.mutateAsync({
        userId: memberId,
        params: values,
      });
      toast({
        message: t("pages.member.profile.updateSaved"),
        intent: "success",
        type: "small",
      });
    },
    validationSchema: toFormikValidationSchema(schema),
    validate: toFormikValidate(schema),
  });
  const { setModal, hide } = useContext(ModalContext);
  const { toast } = useContext(ToastContext);
  return (
    <FormikProvider value={formik}>
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-col gap-y-2 p-6">
          <div className="flex flex-col gap-y-2">
            <FormInput
              name="email"
              type="text"
              placeholder={t("pages.member.profile.email")}
              label={t("pages.member.profile.email")}
            />
            <div className="text-xs text-gray-600">
              {t("pages.member.profile.emailUpdatedDescription")}
            </div>
          </div>
          <FormPasswordInput
            name="password"
            label={t("pages.member.profile.password")}
            placeholder={t("pages.member.profile.password")}
          />

          <FormPasswordInput
            name="passwordConfirm"
            label={t("pages.member.profile.passwordConfirm")}
            placeholder={t("pages.member.profile.passwordConfirm")}
          />
        </div>
        <div className="border-b border-gray-200" />
        <div className="flex w-full flex-row justify-end gap-4 p-6">
          <Button
            intent="link-error"
            onClick={() => {
              setModal(
                <DeleteMemberModal
                  as="staff"
                  memberId={memberId}
                  onConfirm={() => {
                    hide();
                    toast({ message: t("pages.member.profile.userDeleted") });
                    history.push(
                      createClubLink(
                        RouteLayout.Staff,
                        RouteFeature.UserMemberList,
                      ),
                    );
                  }}
                  onClose={() => {
                    hide();
                  }}
                />,
              );
            }}
            className="text-error-500 p-0"
          >
            {t("portal.common.delete")}
          </Button>

          <Button
            intent="secondary"
            onClick={async () => {
              await formik.submitForm();
            }}
            disabled={formik.isSubmitting || !formik.dirty}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
